// Auto-detect CSV column → CIVIX field mapping for the lead importer.
//
// Pre-selects a target field for each CSV header so the user does not have to
// click every dropdown. The user can still override any selection; this only
// proposes mappings.
//
// Matching rules, applied in priority order per header:
//   1. Exact case-insensitive match on the field label
//   2. Exact case-insensitive match on the field key
//   3. Common aliases (first_name → firstName, cell → phone, etc.)
//   4. Slugified equality (strip non-alphanumeric, lowercase) so
//      "First Name" == "first_name" == "FIRST-NAME"
//   5. No match → header is left out of the result (stays unmapped)
//
// Duplicate collisions: if two headers would map to the same field, the first
// header wins and later ones are left unmapped.

// header alias → canonical field key. Keys are slugified (lowercase, alnum only)
// so lookups are tolerant of separators/casing in the source CSV header.
const ALIASES = {
  firstname: 'firstName',
  fname: 'firstName',
  givenname: 'firstName',
  given: 'firstName',
  lastname: 'lastName',
  lname: 'lastName',
  surname: 'lastName',
  familyname: 'lastName',
  // Full Name aliases — server splits into firstName/lastName at import time.
  fullname: 'fullName',
  name: 'fullName',
  contactname: 'fullName',
  leadname: 'fullName',
  customername: 'fullName',
  // Age Range — e.g. "60-74". Kept distinct from plain "age".
  agerange: 'ageRange',
  age_range: 'ageRange',
  agebracket: 'ageRange',
  agebucket: 'ageRange',
  // Current Coverage / Existing Life Insurance — server normalizes to Yes/No/''.
  currentcoverage: 'existingLifeInsurance',
  current_coverage: 'existingLifeInsurance',
  existinglifeinsurance: 'existingLifeInsurance',
  existing_life_insurance: 'existingLifeInsurance',
  hasinsurance: 'existingLifeInsurance',
  has_insurance: 'existingLifeInsurance',
  currentlyinsured: 'existingLifeInsurance',
  currently_insured: 'existingLifeInsurance',
  // Smoker.
  smoker: 'smoker',
  tobacco: 'smoker',
  usestobacco: 'smoker',
  uses_tobacco: 'smoker',
  emailaddress: 'email',
  mail: 'email',
  phonenumber: 'phone',
  cell: 'phone',
  cellphone: 'phone',
  mobile: 'phone',
  mobilephone: 'phone',
  tel: 'phone',
  telephone: 'phone',
  primaryphone: 'phone',
  callerid: 'phone',
  zipcode: 'zip',
  postalcode: 'zip',
  postal: 'zip',
  dateofbirth: 'dob',
  birthdate: 'dob',
  birthday: 'dob',
  province: 'state',
  region: 'state',
  town: 'city',
};

function slugify(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Propose a header → field-key mapping for the given CSV headers.
 *
 * @param {string[]} csvHeaders        Header strings parsed from the CSV.
 * @param {{ key: string, label: string }[]} targetFields  Available CIVIX fields.
 * @returns {Record<string, string>}   Map of CSV header → target field key.
 *   Only headers that matched a field are included.
 */
export function autoDetectColumnMapping(csvHeaders = [], targetFields = []) {
  // Build lookup tables once.
  const byLabel = new Map();   // slug(label) → key
  const byKey = new Map();     // slug(key)   → key
  for (const field of targetFields) {
    if (!field || !field.key) continue;
    if (field.label) byLabel.set(slugify(field.label), field.key);
    byKey.set(slugify(field.key), field.key);
  }

  const validKeys = new Set(byKey.values());
  const result = {};
  const usedKeys = new Set();

  for (const header of csvHeaders) {
    if (header == null || header === '') continue;
    if (header in result) continue; // identical header text already handled
    const slug = slugify(header);
    if (!slug) continue;

    // Rule 1 → 2 → 3 → 4 (slug captures case-insensitivity for all of them).
    let matchedKey =
      byLabel.get(slug) ||
      byKey.get(slug) ||
      ALIASES[slug] ||
      null;

    // An alias may resolve to a field key that this importer instance does not
    // expose. Never propose a key the caller didn't offer.
    if (matchedKey && !validKeys.has(matchedKey)) matchedKey = null;

    if (!matchedKey) continue;            // Rule 5: leave unmapped
    if (usedKeys.has(matchedKey)) continue; // duplicate collision: first wins

    result[header] = matchedKey;
    usedKeys.add(matchedKey);
  }

  return result;
}

export default autoDetectColumnMapping;
